/**
 * Home's rows and a title page's Similar / You May Also Like from the discovery files, over
 * titles the viewer's catalog already has (roadmap Section 26 step 4; ADR "Home rows and title
 * pages from open data"). Pure logic, no React, no network.
 *
 * This is the website's copy of the rules the APK runs natively in DiscoveryRows.java
 * (native/android/app/.../catalog/DiscoveryRows.java). The two must agree: every rule here
 * is the same rule there, and test/discoveryPick.test.ts repeats DiscoveryRowsTest.java's cases.
 *
 * - Rows claim titles in Home order (Trending, Popular, the genre rows, Spotlight), so no title
 *   is on two of them the same day.
 * - Each row shows one group of its pool (about 60 titles) a day: day N shows group N mod groups,
 *   so a reload the same day shows the same rows.
 * - Spotlight is exactly one full row: its type follows a fixed daily cycle and its subject
 *   rotates within the type; a type with less than a full row left is skipped.
 * - A Kids profile sees only Kids-safe titles, in every row.
 * - Trending, Popular and the genre rows are US titles only (origin United States, original
 *   language English; owner, 2026-10-11): a title meta.json does not describe is left out.
 */

/** Flags in meta.json (scripts/discovery/lib.mjs FLAG). */
export const FLAG = {
  US: 1,
  ANIMATED: 2,
  CHILDREN: 4,
  FAMILY: 8,
  TRUE_STORY: 16,
  FROM_BOOK: 32,
  OSCAR: 64,
  EMMY: 128,
} as const;

/** meta.json's genre words, as bits (lib.mjs GENRE_RULES; a word not listed is ignored). */
export const GENRES = [
  "scifi", "romance", "comedy", "drama", "action", "adventure", "animated", "children", "family", "horror",
  "thriller", "crime", "mystery", "fantasy", "documentary", "biographical", "history", "war", "western",
  "music", "superhero", "teen", "sports", "reality", "talk", "news",
] as const;

export function genreBit(word: string): number {
  const index = (GENRES as readonly string[]).indexOf(word);
  return index < 0 ? 0 : 1 << index;
}

const TALK_NEWS_REALITY = genreBit("talk") | genreBit("news") | genreBit("reality");

/** Home's genre rows, in Home order (packages/tmdb-client SHELF_IDS). */
export const SHELVES = ["family", "drama", "kids", "action", "comedy", "scifi", "romance"] as const;

/** Titles a row's daily groups are drawn from. */
export const POOL = 60;
/** The quality floor the genre rows put first, as the catalog top-up does. */
export const FLOOR = 6.5;

/** One title the catalog has, with what the discovery files say about it. */
export interface PickTitle {
  tmdb: number;
  year: number;
  genres: number;
  flags: number;
  mpa: string | null;
  franchise: number;
  people: number[];
  /** The main cast (Wikidata P161), for Similar. */
  cast: number[];
  /** Production companies (Wikidata P272): Pixar, Walt Disney Animation Studios, Marvel Studios. */
  studios: number[];
  views: number;
  /** The provider's rating, or NaN. */
  rating: number;
  /** In one of the provider's Kids / Animation categories. */
  kidsCategory: boolean;
  /** Known to meta.json (a Trending title outside it has no genres, rating or flags). */
  described: boolean;
}

export function blankTitle(tmdb: number): PickTitle {
  return {
    tmdb, year: 0, genres: 0, flags: 0, mpa: null, franchise: 0, people: [], cast: [], studios: [], views: 0,
    rating: Number.NaN, kidsCategory: false, described: false,
  };
}

export interface PickRequest {
  /** Days since the epoch in the viewer's time zone (shelfFill.ts localDayIndex). */
  day: number;
  /** Tiles per row (Settings: 12 or 24). */
  size: number;
  kids: boolean;
}

export interface PickSpotlight {
  /** award, trueStory, fromBook, franchise, studio, decade, person */
  type: string;
  title: string;
  tmdb: number[];
}

export interface PickResult {
  /** Row id (trending, popular, a shelf id) → TMDB ids in order. */
  rows: Map<string, number[]>;
  spotlight: PickSpotlight | null;
}

const SPOTLIGHT_TYPES = ["award", "trueStory", "fromBook", "franchise", "studio", "decade", "person"] as const;
/** Titles sent after Spotlight's row, for the page to fill from if a match fails. */
const SPOTLIGHT_SPARES = 6;

const rated = (t: PickTitle): boolean => !Number.isNaN(t.rating) && t.rating >= FLOOR;
const floorMod = (a: number, n: number): number => ((a % n) + n) % n;

/**
 * Kids-safe (ADR, review 2026-10-10): animated and rated G or PG, OR a children's title,
 * OR in the provider's Kids / Animation categories, and never rated PG-13 or above.
 */
export function kidsSafe(t: PickTitle): boolean {
  if (t.mpa === "PG-13" || t.mpa === "R" || t.mpa === "NC-17") return false;
  const animatedLow = (t.flags & FLAG.ANIMATED) !== 0 && (t.mpa === "G" || t.mpa === "PG");
  return animatedLow || (t.flags & FLAG.CHILDREN) !== 0 || t.kidsCategory;
}

/** The US-only rule: origin United States and original language English, as meta.json flags it. */
export function us(t: PickTitle): boolean {
  return t.described && (t.flags & FLAG.US) !== 0;
}

/** Rows a fully animated title may be on (owner, 2026-10-11); live action with CGI may be on any. */
const animationAllowed = (shelf: string): boolean => shelf === "family" || shelf === "kids";

/** Whether a title belongs on a genre row: the US-only rule first, as TMDB's rows had it. */
export function onShelf(shelf: string, t: PickTitle): boolean {
  if (!us(t)) return false;
  if ((t.flags & FLAG.ANIMATED) !== 0 && !animationAllowed(shelf)) return false;
  switch (shelf) {
    case "family":
      return (t.flags & FLAG.FAMILY) !== 0 && !kidsSafe(t);
    case "drama":
      return (t.genres & genreBit("drama")) !== 0 && (t.genres & TALK_NEWS_REALITY) === 0;
    case "kids":
      return kidsSafe(t);
    case "action":
      return (t.genres & (genreBit("action") | genreBit("adventure"))) !== 0;
    case "comedy":
      return (t.genres & genreBit("comedy")) !== 0 && (t.genres & TALK_NEWS_REALITY) === 0;
    case "scifi":
      return (t.genres & genreBit("scifi")) !== 0;
    case "romance":
      return (t.genres & genreBit("romance")) !== 0;
    default:
      return false;
  }
}

/**
 * The day's group of a pool, skipping titles already on Home: the pool is cut into rows of
 * `size`, and day N shows row N mod rows. The last group wraps to the pool's start, so a row is
 * never half empty while the pool has enough.
 */
export function group(pool: readonly number[], used: ReadonlySet<number>, day: number, size: number): number[] {
  const fresh: number[] = [];
  const seen = new Set<number>();
  for (const id of pool) {
    if (used.has(id) || seen.has(id)) continue;
    seen.add(id);
    fresh.push(id);
  }
  if (fresh.length <= size) return fresh;
  const groups = Math.ceil(fresh.length / size);
  const start = floorMod(day, groups) * size;
  const out: number[] = [];
  for (let i = 0; i < size; i += 1) out.push(fresh[(start + i) % fresh.length]!);
  return out;
}

/** The rating a title without one is taken to have: the floor, so it neither gains nor loses. */
const NEUTRAL_RATING = FLOOR;
/** How much one rating point is worth, in powers of ten of views: half (each point is about 3.2 times the views). */
const POINT = 0.5;

/**
 * Popular's blend of how many watch it and how good it is: log10 of 90-day views, plus half an
 * order of magnitude per rating point above the floor (minus below it). A title the provider
 * gives no rating counts as the floor.
 */
export function popularScore(t: PickTitle): number {
  const rating = Number.isNaN(t.rating) ? NEUTRAL_RATING : t.rating;
  return Math.log10(Math.max(1, t.views)) + POINT * (rating - NEUTRAL_RATING);
}

/** Well-rated first, then the rest; each by 90-day views, best first. */
function ranked(titles: Iterable<PickTitle>): PickTitle[] {
  const good: PickTitle[] = [];
  const rest: PickTitle[] = [];
  for (const t of titles) (rated(t) ? good : rest).push(t);
  const byViews = (a: PickTitle, b: PickTitle) => (b.views !== a.views ? b.views - a.views : a.tmdb - b.tmdb);
  good.sort(byViews);
  rest.sort(byViews);
  return [...good, ...rest];
}

const ids = (titles: readonly PickTitle[], limit: number): number[] => titles.slice(0, limit).map((t) => t.tmdb);

/**
 * The rows. `available` is every title the catalog has that a row could show (TMDB id → title);
 * `trending` and `popular` are the daily file's lists in their order; `names` names franchises
 * and people (meta.json's names table).
 */
export function pick(
  request: PickRequest,
  available: ReadonlyMap<number, PickTitle>,
  trending: readonly number[],
  popular: readonly number[],
  names: ReadonlyMap<number, string>,
  films: boolean,
): PickResult {
  const rows = new Map<string, number[]>();
  const used = new Set<number>();

  const trendingPool: number[] = [];
  for (const id of trending) {
    const t = available.get(id);
    if (!t || !us(t) || (request.kids && !kidsSafe(t))) continue;
    trendingPool.push(id);
    if (trendingPool.length === POOL) break;
  }
  // The row keeps the daily file's order within its group: the hero draws from its first titles.
  const trendingRow = group(trendingPool, used, request.day, request.size);
  for (const id of trendingRow) used.add(id);
  rows.set("trending", trendingRow);

  const popularTitles: PickTitle[] = [];
  for (const id of popular) {
    const t = available.get(id);
    if (!t || !us(t) || (request.kids && !kidsSafe(t))) continue;
    popularTitles.push(t);
  }
  // Popular: the provider's rating blended with 90-day views (ADR; owner, 2026-10-11: "use a blend").
  popularTitles.sort((a, b) => {
    const byScore = popularScore(b) - popularScore(a);
    return byScore !== 0 ? byScore : b.views - a.views;
  });
  const popularPool: number[] = [];
  for (const t of popularTitles) if (!used.has(t.tmdb) && popularPool.length < POOL) popularPool.push(t.tmdb);
  const popularRow = group(popularPool, used, request.day, request.size);
  for (const id of popularRow) used.add(id);
  rows.set("popular", popularRow);

  for (const shelf of SHELVES) {
    const fits: PickTitle[] = [];
    for (const t of available.values()) {
      if (used.has(t.tmdb) || !onShelf(shelf, t) || (request.kids && !kidsSafe(t))) continue;
      fits.push(t);
    }
    const row = group(ids(ranked(fits), POOL), used, request.day, request.size);
    for (const id of row) used.add(id);
    rows.set(shelf, row);
  }

  const spotlightRow = spotlight(request, available, used, names, films);
  return { rows, spotlight: spotlightRow };
}

// --- A title page's Similar and You May Also Like (Section 26 step 4) ---

export interface Related {
  similar: number[];
  youMayLike: number[];
}

/** Trending titles You May Also Like mixes in (ADR: "some Trending mixed in"). */
const RELATED_TRENDING = 2;

function shared(a: readonly number[], b: readonly number[]): number {
  let n = 0;
  for (const x of a) for (const y of b) if (x === y) n += 1;
  return n;
}

function bitCount(value: number): number {
  let n = 0;
  let v = value >>> 0;
  while (v !== 0) {
    n += v & 1;
    v >>>= 1;
  }
  return n;
}

/**
 * How like the open title another is (ADR: "same franchise or sequel heaviest, then genre
 * overlap, shared director or cast, year closeness, rating"). Animation keeps to animation and
 * live action to live action. Zero when nothing ties them (no franchise, no person, no genre in
 * common): such a title is never Similar.
 */
export function similarity(seed: PickTitle, t: PickTitle): number {
  const franchise = seed.franchise !== 0 && seed.franchise === t.franchise;
  const genres = bitCount(seed.genres & t.genres);
  const people = shared(seed.people, t.people);
  if (!franchise && genres === 0 && people === 0) return 0;
  let score = (franchise ? 100 : 0) + genres * 10 + people * 25 + shared(seed.cast, t.cast) * 6 + shared(seed.studios, t.studios) * 4;
  if (((seed.flags ^ t.flags) & FLAG.ANIMATED) !== 0) score -= 15;
  if (seed.year > 0 && t.year > 0) score += Math.max(0, 10 - Math.abs(seed.year - t.year) / 2);
  if (!Number.isNaN(t.rating)) score += (t.rating - FLOOR) * 2;
  score += Math.log10(Math.max(1, t.views));
  return Math.max(score, 0.001);
}

/** How much of You May Also Like follows the profile's taste, by how many titles it has. */
export function tasteShare(titles: number): number {
  return titles < 3 ? 0 : titles < 8 ? 0.25 : titles < 15 ? 0.5 : 0.75;
}

/** Whether a title shares anything with what the profile chose: genres, people, cast, studios, franchise. */
function tasteOverlap(taste: readonly PickTitle[], t: PickTitle): number {
  let overlap = 0;
  for (const liked of taste) {
    overlap +=
      bitCount(liked.genres & t.genres) +
      shared(liked.people, t.people) +
      shared(liked.cast, t.cast) +
      shared(liked.studios, t.studios) +
      (liked.franchise !== 0 && liked.franchise === t.franchise ? 1 : 0);
  }
  return overlap;
}

/** How well a title fits the profile's taste: the genres, people, franchises and studios it keeps choosing. */
function tasteScore(taste: readonly PickTitle[], t: PickTitle): number {
  let score = 0;
  for (const liked of taste) {
    score +=
      bitCount(liked.genres & t.genres) * 2 +
      shared(liked.people, t.people) * 6 +
      shared(liked.cast, t.cast) * 2 +
      shared(liked.studios, t.studios) +
      (liked.franchise !== 0 && liked.franchise === t.franchise ? 8 : 0);
  }
  if (!Number.isNaN(t.rating)) score += (t.rating - FLOOR) * taste.length * 0.5;
  return score;
}

/**
 * Similar: the titles most like the open one, best first. You May Also Like: Similar's next
 * titles, a few of this week's Trending that share a genre, and, as the profile's history grows,
 * its taste (tasteShare); nothing watched or on My List (`drop`), nothing in Similar.
 */
export function related(
  seed: PickTitle,
  available: ReadonlyMap<number, PickTitle>,
  tasteTmdb: readonly number[],
  drop: ReadonlySet<number>,
  trending: readonly number[],
  size: number,
  kids: boolean,
): Related {
  const out: Related = { similar: [], youMayLike: [] };
  const score = new Map<PickTitle, number>();
  const candidates: PickTitle[] = [];
  for (const t of available.values()) {
    if (t.tmdb === seed.tmdb || !t.described || (kids && !kidsSafe(t))) continue;
    const s = similarity(seed, t);
    if (s <= 0) continue;
    score.set(t, s);
    candidates.push(t);
  }
  candidates.sort((a, b) => {
    const by = score.get(b)! - score.get(a)!;
    return by !== 0 ? by : a.tmdb - b.tmdb;
  });
  const taken = new Set<number>([seed.tmdb]);
  for (const t of candidates) {
    if (out.similar.length === size) break;
    out.similar.push(t.tmdb);
    taken.add(t.tmdb);
  }

  const taste: PickTitle[] = [];
  for (const id of tasteTmdb) {
    const t = available.get(id);
    if (t && t.described && !taste.includes(t)) taste.push(t);
  }
  const fromTaste = Math.round(size * tasteShare(taste.length));
  const liked: number[] = [];
  if (fromTaste > 0) {
    const fit = new Map<PickTitle, number>();
    const pool: PickTitle[] = [];
    for (const t of available.values()) {
      if (taken.has(t.tmdb) || drop.has(t.tmdb) || !t.described || (kids && !kidsSafe(t))) continue;
      const f = tasteScore(taste, t);
      if (f <= 0) continue;
      fit.set(t, f);
      pool.push(t);
    }
    pool.sort((a, b) => {
      const by = fit.get(b)! - fit.get(a)!;
      return by !== 0 ? by : a.tmdb - b.tmdb;
    });
    for (const t of pool) {
      if (liked.length === fromTaste) break;
      liked.push(t.tmdb);
    }
  }
  const trend: number[] = [];
  for (const id of trending) {
    if (trend.length === RELATED_TRENDING) break;
    const t = available.get(id);
    if (!t || !t.described || taken.has(id) || drop.has(id) || liked.includes(id)) continue;
    if ((t.genres & seed.genres) === 0 || (kids && !kidsSafe(t))) continue;
    trend.push(id);
  }
  const next: number[] = [];
  for (const t of candidates) {
    if (taken.has(t.tmdb) || drop.has(t.tmdb) || liked.includes(t.tmdb) || trend.includes(t.tmdb)) continue;
    next.push(t.tmdb);
  }
  // Interleaved so the row reads as one: taste, then like-this, with Trending second and fifth.
  let n = 0;
  let l = 0;
  let x = 0;
  let tr = 0;
  while (out.youMayLike.length < size && (l < liked.length || x < next.length || tr < trend.length)) {
    let id: number;
    if ((n === 1 || n === 4) && tr < trend.length) id = trend[tr++]!;
    else if (n % 2 === 0 && l < liked.length) id = liked[l++]!;
    else if (x < next.length) id = next[x++]!;
    else if (l < liked.length) id = liked[l++]!;
    else id = trend[tr++]!;
    if (!out.youMayLike.includes(id)) out.youMayLike.push(id);
    n += 1;
  }
  return out;
}

// --- Recommended For You (Home; owner, 2026-10-12) ---

/**
 * The row on Home that replaces My List: for one side (films or series), titles chosen for the
 * profile. A fresh start has no history, so it gets the TOP titles of each genre, one genre
 * after another (the genre rows' own rules and ranking: US, quality floor first, then 90-day
 * views); from there it is built the way You May Also Like is, as the profile's history and My
 * List grow: a share of the row follows taste (tasteShare of how many titles the profile has,
 * films and series together), a couple of this week's Trending are mixed in, the rest are the
 * top titles of each genre. Never a title the profile watched or has on My List, and a Kids
 * profile sees only Kids-safe titles.
 *
 * It rotates daily (owner, 2026-10-12): the ranked pool (about 60 titles) is cut into rows of
 * `size` and day N shows row N mod rows, as every other Home row does, so a reload the same
 * day shows the same row.
 *
 * `tasteTotal` is how many titles the profile has in all, on both sides, so the share is the
 * same for films and series; `tasteTmdb` are this side's.
 */
export function recommended(
  available: ReadonlyMap<number, PickTitle>,
  tasteTmdb: readonly number[],
  drop: ReadonlySet<number>,
  trending: readonly number[],
  size: number,
  kids: boolean,
  tasteTotal: number,
  day: number,
): number[] {
  const taste: PickTitle[] = [];
  for (const id of tasteTmdb) {
    const t = available.get(id);
    if (t && t.described && !taste.includes(t)) taste.push(t);
  }
  const taken = new Set<number>();

  // What the profile's taste picks: the titles that share its genres, people, franchises or
  // studios, best fit first (a title tied to nothing it chose is never a taste pick).
  const share = tasteShare(tasteTotal);
  const liked: number[] = [];
  if (share > 0 && taste.length > 0) {
    const fit = new Map<PickTitle, number>();
    const pool: PickTitle[] = [];
    for (const t of available.values()) {
      if (drop.has(t.tmdb) || !t.described || !us(t) || (kids && !kidsSafe(t))) continue;
      if (tasteOverlap(taste, t) <= 0) continue;
      fit.set(t, tasteScore(taste, t));
      pool.push(t);
    }
    pool.sort((a, b) => {
      const by = fit.get(b)! - fit.get(a)!;
      return by !== 0 ? by : a.tmdb - b.tmdb;
    });
    for (const t of pool) {
      if (liked.length === Math.round(POOL * share)) break;
      liked.push(t.tmdb);
      taken.add(t.tmdb);
    }
  }

  // This week's Trending, a couple of them (as You May Also Like mixes in).
  const trend: number[] = [];
  for (const id of trending) {
    if (trend.length === RELATED_TRENDING) break;
    const t = available.get(id);
    if (!t || !us(t) || drop.has(id) || taken.has(id) || (kids && !kidsSafe(t))) continue;
    trend.push(id);
    taken.add(id);
  }

  // The top titles of each genre, one genre after another: every genre's best, then every
  // genre's second best, until the pool is full.
  const lists: PickTitle[][] = SHELVES.map((shelf) => {
    const fits: PickTitle[] = [];
    for (const t of available.values()) {
      if (drop.has(t.tmdb) || taken.has(t.tmdb) || !onShelf(shelf, t) || (kids && !kidsSafe(t))) continue;
      fits.push(t);
    }
    return ranked(fits).slice(0, POOL);
  });
  const next: number[] = [];
  for (let rank = 0; rank < POOL && next.length < POOL; rank += 1) {
    for (const list of lists) {
      const t = list[rank];
      if (t && !taken.has(t.tmdb) && !next.includes(t.tmdb)) next.push(t.tmdb);
      if (next.length === POOL) break;
    }
  }

  // One ordered pool: taste at its share of every row, top titles between, Trending second and
  // fifth of the first row.
  const ordered: number[] = [];
  let l = 0;
  let x = 0;
  let tr = 0;
  for (let n = 0; ordered.length < POOL && (l < liked.length || x < next.length || tr < trend.length); n += 1) {
    let id: number;
    if ((n === 1 || n === 4) && tr < trend.length) id = trend[tr++]!;
    else if (l < liked.length && l < Math.round((n + 1) * share)) id = liked[l++]!;
    else if (x < next.length) id = next[x++]!;
    else if (l < liked.length) id = liked[l++]!;
    else id = trend[tr++]!;
    if (!ordered.includes(id)) ordered.push(id);
  }
  return group(ordered, new Set(), day, size);
}

/**
 * One row from the two sides' picks: a film, a series, a film, a series, until the row is full;
 * when one side runs out, the other fills on (a profile with no series on offer still gets a row).
 */
export function alternate<T>(films: readonly T[], series: readonly T[], size: number): T[] {
  const out: T[] = [];
  let f = 0;
  let s = 0;
  while (out.length < size && (f < films.length || s < series.length)) {
    if (f < films.length && (out.length % 2 === 0 || s >= series.length)) out.push(films[f++]!);
    else if (s < series.length) out.push(series[s++]!);
    else out.push(films[f++]!);
  }
  return out;
}

// --- Spotlight ---

/** One subject of a Spotlight type: its title and the titles that fit it. */
interface Subject {
  title: string;
  titles: PickTitle[];
}

/**
 * Today's Spotlight: the cycle starts at the type for the day; within a type the subject is
 * chosen by the cycle number, so the same type shows a new franchise, decade or director each
 * time round. A type with no subject left that fills a whole row is skipped.
 */
export function spotlight(
  request: PickRequest,
  available: ReadonlyMap<number, PickTitle>,
  used: Set<number>,
  names: ReadonlyMap<number, string>,
  films: boolean,
): PickSpotlight | null {
  const left: PickTitle[] = [];
  for (const t of available.values()) {
    if (!t.described || used.has(t.tmdb) || (request.kids && !kidsSafe(t))) continue;
    left.push(t);
  }
  const count = SPOTLIGHT_TYPES.length;
  const cycle = Math.floor(request.day / count);
  const first = floorMod(request.day, count);
  for (let i = 0; i < count; i += 1) {
    const type = SPOTLIGHT_TYPES[(first + i) % count]!;
    const subjects = subjectsOf(type, left, names, films).filter((subject) => subject.titles.length >= request.size);
    if (subjects.length === 0) continue;
    const subject = subjects[floorMod(cycle, subjects.length)]!;
    const pool = ids(ranked(subject.titles), POOL);
    const tmdb = group(pool, used, cycle, request.size);
    if (tmdb.length < request.size) continue;
    // A few spares after the row, so the page still has a full row when the catalog match drops
    // a title or two (the page shows exactly one row's worth).
    for (const id of pool) {
      if (tmdb.length >= request.size + SPOTLIGHT_SPARES) break;
      if (!tmdb.includes(id)) tmdb.push(id);
    }
    for (const id of tmdb) used.add(id);
    return { type, title: subject.title, tmdb };
  }
  return null;
}

/** Subjects keyed by a number, in key order (Java's TreeMap). */
function keyed(by: Map<number, Subject>): Subject[] {
  return [...by.entries()].sort((a, b) => a[0] - b[0]).map(([, subject]) => subject);
}

function subjectsOf(type: string, left: readonly PickTitle[], names: ReadonlyMap<number, string>, films: boolean): Subject[] {
  switch (type) {
    case "award": {
      const flag = films ? FLAG.OSCAR : FLAG.EMMY;
      return [{ title: films ? "Oscar Winners" : "Emmy Winners", titles: left.filter((t) => (t.flags & flag) !== 0) }];
    }
    case "trueStory":
      return [{ title: "Based on a True Story", titles: left.filter((t) => (t.flags & FLAG.TRUE_STORY) !== 0) }];
    case "fromBook":
      return [{ title: "From Page to Screen", titles: left.filter((t) => (t.flags & FLAG.FROM_BOOK) !== 0) }];
    case "franchise": {
      const by = new Map<number, Subject>();
      for (const t of left) {
        const name = t.franchise === 0 ? undefined : names.get(t.franchise);
        if (name === undefined) continue;
        const subject = by.get(t.franchise) ?? { title: franchiseTitle(name), titles: [] };
        subject.titles.push(t);
        by.set(t.franchise, subject);
      }
      return keyed(by);
    }
    case "studio": {
      // One studio (owner, 2026-10-11: "no reason not to have Disney or Pixar in the spotlight").
      const by = new Map<number, Subject>();
      for (const t of left) {
        for (const studio of t.studios) {
          const name = names.get(studio);
          if (name === undefined) continue;
          const subject = by.get(studio) ?? { title: `From ${name}`, titles: [] };
          subject.titles.push(t);
          by.set(studio, subject);
        }
      }
      return keyed(by);
    }
    case "decade": {
      const by = new Map<number, Subject>();
      for (const t of left) {
        if (t.year < 1900) continue;
        const decade = Math.floor(t.year / 10) * 10;
        const subject = by.get(decade) ?? { title: `The ${decade}s`, titles: [] };
        subject.titles.push(t);
        by.set(decade, subject);
      }
      return keyed(by);
    }
    case "person": {
      const by = new Map<number, Subject>();
      for (const t of left) {
        for (const person of t.people) {
          const name = names.get(person);
          if (name === undefined) continue;
          const subject = by.get(person) ?? { title: `${films ? "Directed by " : "Created by "}${name}`, titles: [] };
          subject.titles.push(t);
          by.set(person, subject);
        }
      }
      return keyed(by);
    }
    default:
      return [];
  }
}

/** "Harry Potter film series" → "Harry Potter"; Wikidata names franchises by what they are. */
export function franchiseTitle(name: string): string {
  const trimmed = name.replace(/\s+(film series|film franchise|media franchise|franchise|series|trilogy|saga)$/i, "").trim();
  return trimmed === "" ? name : trimmed;
}
